import type { Product } from "~data/Product";

import { useDebouncedEffect } from "@react-hookz/web";
import { isEqual } from "lodash";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";

import ApplicationLayout from "../../components/layouts/Application";
import PasswordModal from "../../components/layouts/partials/PasswordModal";
import t from "../../i18n";
import { path } from "../../paths";
import { useApiData } from "../../use/api";
import { useCurrentTeamId } from "../../use/currentTeamId";
import useRedirect from "../../use/redirect";
import { useAuthentication } from "../AuthenticationProvider";
import { useModal } from "../ModalProvider";
import { useNotifications } from "../NotificationsProvider";
import {
  getCurrentProduct,
  SidebarItem,
  useHqNaviation,
  usePrimaryNavigation,
  useSecondaryNavigation,
} from "./application/navigation";
import useTopBar from "./application/top-bar";

type ApplicationLayoutOptions = Pick<
  React.ComponentProps<typeof ApplicationLayout>,
  "currentTitle" | "fixedHeight" | "hiddenSidebar"
> & {
  currentRoute?: SidebarItem["id"];
};

const ApplicationLayoutContext = createContext({
  configureLayout: (options: ApplicationLayoutOptions) => {},
  currentProduct: undefined as Product | undefined,
});

export default function ApplicationLayoutProvider({ children }: { children: React.ReactNode }) {
  const [applicationLayoutOptions, setApplicationLayoutOptions] = useState<ApplicationLayoutOptions>({});
  const [applicationLayoutOptionsDebounced, setApplicationLayoutOptionsDebounced] = useState<ApplicationLayoutOptions>(
    {},
  );

  const { adminRoles, logout, promptForMfa, promptForPassword, teamMemberships, teams, user } = useAuthentication();
  const notifications = useNotifications();
  const { addModal } = useModal();
  const location = useLocation();
  const redirect = useRedirect();

  const route = useMatch(location.pathname);

  const configureApplicationLayout = useCallback((options: Partial<ApplicationLayoutOptions>) => {
    setTimeout(() => {
      // Cannot be called synchronously, otherwise it will cause a re-render loop
      setApplicationLayoutOptions((previousOptions) => {
        if (isEqual({ ...previousOptions, ...options }, previousOptions)) return previousOptions;

        return { ...previousOptions, ...options };
      });
    });
  }, []);

  useEffect(() => {
    setApplicationLayoutOptions({});
  }, [route]);

  useEffect(() => {
    if (location.pathname === path("up.auth.mfa.enable")) return;
    if (promptForMfa) {
      addModal({
        content: t("Please set up your MFA before continuing."),
        onClick: () => {
          redirect(["up.auth.mfa.enable"]);
        },
        title: t("MFA setup required"),
      });
    }
  }, [promptForMfa, addModal, redirect, location]);

  useDebouncedEffect(
    () => {
      setApplicationLayoutOptionsDebounced(applicationLayoutOptions);
    },
    [applicationLayoutOptions],
    200,
  );

  const currentTeamId = useCurrentTeamId(true) ?? undefined;

  const currentProduct = useMemo(() => {
    return getCurrentProduct({
      currentTeamId: currentTeamId,
      hostName: window.location.host,
      teams: teams!,
    });
  }, [teams, currentTeamId]);

  const { game } = useApiData("global.gamification.show");

  const topBar = useTopBar({
    currentProduct: currentProduct ?? null,
    currentRoute: applicationLayoutOptionsDebounced.currentRoute ?? null,
  });

  const primaryNavigationItems = usePrimaryNavigation({
    currentProduct: currentProduct,
    currentRoute: applicationLayoutOptionsDebounced.currentRoute,
    currentTeamId: currentTeamId,
    isAdmin: user!.isAdmin,
    teamMemberships: teamMemberships!,
  });

  const hqNavigationItems = useHqNaviation({
    adminRoles,
    currentRoute: applicationLayoutOptionsDebounced.currentRoute,
  });

  const secondaryNavigationItems = useSecondaryNavigation({
    currentProduct,
    currentTeamId: currentTeamId,
    primaryNavigation: primaryNavigationItems,
    teams: teams!,
  });

  const availableProducts = useMemo(() => {
    return new Set<Product>(teams!.map((t) => t.product).filter((p) => p !== currentProduct));
  }, [teams, currentProduct]);

  return (
    <ApplicationLayoutContext.Provider
      value={{ configureLayout: configureApplicationLayout, currentProduct: currentProduct }}
    >
      {promptForPassword && <PasswordModal />}
      <ApplicationLayout
        auth={{ email: user!.email }}
        availableProducts={availableProducts}
        currentTitle={applicationLayoutOptionsDebounced.currentTitle}
        fixedHeight={applicationLayoutOptionsDebounced.fixedHeight}
        gamification={currentProduct === "helloscreen" ? game : null}
        hiddenSidebar={applicationLayoutOptionsDebounced.hiddenSidebar}
        notifications={notifications}
        onLogout={logout}
        primaryNavigation={
          hqNavigationItems.length > 0 ? { items: hqNavigationItems } : { items: primaryNavigationItems }
        }
        product={currentProduct}
        secondaryNavigation={
          secondaryNavigationItems
            ? {
                items: secondaryNavigationItems,
                title: "Teams",
              }
            : undefined
        }
        topBar={topBar}
      >
        {children}
      </ApplicationLayout>
    </ApplicationLayoutContext.Provider>
  );
}

export function useApplicationLayout() {
  return useContext(ApplicationLayoutContext);
}
